import { IconHelp, Tooltip } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, ReactNode, isValidElement, memo } from 'react';
import styles from './CardTable.module.scss';
import { useIsClamped } from '@/hooks/useIsClamped';

const cx = classnames.bind(styles);

export type Props = {
  label: ReactNode;
  value: ReactNode;
  align?: 'left' | 'right';
  unit?: ReactNode;
  caption?: ReactNode;
  help?: ReactNode;
};

/**
 * Column `render()` functions shared with the Table layout often wrap plain
 * values in a `<TooltipCell title={...}>` element before they reach `Cell`.
 * Rendering that element again inside our own tooltip would nest a
 * second, always-on hover tooltip inside this one, so prefer its `title`
 * (the original plain value) when present.
 */
const getTooltipValue = (value: ReactNode): ReactNode =>
  isValidElement<{ title?: ReactNode }>(value) && 'title' in value.props
    ? value.props.title
    : value;

export const Cell: FC<Props> = memo(
  ({ label, value, align = 'left', unit, caption, help }) => {
    // Unwrap a `<TooltipCell title={...}>` (shared with the Table layout)
    // to its plain content: rendering it as-is would nest its own
    // always-mounted `Tooltip` inside `valueNode`, which fires on hover of
    // the same spot and covers this component's unified tooltip.
    const displayValue = getTooltipValue(value);
    const { ref, isClamped } = useIsClamped<HTMLDivElement>(value);
    const { ref: labelRef, isClamped: isLabelClamped } =
      useIsClamped<HTMLSpanElement>(label);
    const { ref: captionRef, isClamped: isCaptionClamped } =
      useIsClamped<HTMLSpanElement>(caption);

    const labelNode = (
      <span ref={labelRef} className={cx('label-text')}>
        {label}
      </span>
    );
    const valueNode = (
      <div ref={ref} className={cx('value', { withCaption: Boolean(caption) })}>
        {displayValue}
      </div>
    );
    const captionNode = caption && (
      <span ref={captionRef} className={cx('caption')}>
        {caption}
      </span>
    );

    // Columns that also carry a `help` tooltip (借方/貸方仕入控除税額) keep
    // their pre-existing per-part tooltips: folding `help` into the unified
    // truncation tooltip below is still unresolved (NA-5596, NEED CONFIRM).
    if (help) {
      return (
        <div className={cx('cell')}>
          <dt className={cx('label')}>
            <Tooltip title={isLabelClamped ? label : undefined}>
              {labelNode}
            </Tooltip>
            <Tooltip title={help}>
              <span className={cx('label-help')}>
                <IconHelp />
              </span>
            </Tooltip>
          </dt>
          <dd className={cx('content', { 'align-right': align === 'right' })}>
            <div className={cx('value-block')}>
              <Tooltip title={isClamped ? displayValue : undefined}>
                {valueNode}
              </Tooltip>
              {captionNode}
            </div>
            {unit && <span className={cx('unit')}>{unit}</span>}
          </dd>
        </div>
      );
    }

    // NA-5596: a single tooltip covering label + value + code, shown only
    // when at least one of the three is truncated, rather than one tooltip
    // per part. Per Figma, the label sits on its own line above value(code),
    // not inline.
    const isTruncated = isClamped || isLabelClamped || isCaptionClamped;
    const tooltipTitle = isTruncated ? (
      <>
        {label}:<br />
        {displayValue}
        {caption ? <>({caption})</> : null}
      </>
    ) : undefined;

    return (
      <Tooltip title={tooltipTitle}>
        <div className={cx('cell')}>
          <dt className={cx('label')}>{labelNode}</dt>
          <dd className={cx('content', { 'align-right': align === 'right' })}>
            <div className={cx('value-block')}>
              {valueNode}
              {captionNode}
            </div>
            {unit && <span className={cx('unit')}>{unit}</span>}
          </dd>
        </div>
      </Tooltip>
    );
  }
);
Cell.displayName = 'Cell';

/**
 * Empty cell padding out the last row. Renders borders only.
 */
export const FillerCell: FC = memo(() => (
  <div className={cx('cell')} aria-hidden='true' />
));
FillerCell.displayName = 'FillerCell';
